import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, toast } from '@gitbook/spine-react';

import { useNavigationState } from '../../navigation';
import { apiClient, apiQuery } from '../api';
import { getUpgradeScreenNavigationTarget } from './useNavigateToUpgradeScreen';

type SiteCheckoutState = {
    buttonProps: Pick<ButtonProps, 'label' | 'leadingIcon' | 'trailingIcon' | 'kind' | 'href'>;
    onPress: () => Promise<api.Site | null>;
    state: api.OperationSpec<'previewSitePublishCheckout'>['ResponseBody'];
};

/**
 * Returns the checkout state of a site.
 */
export function useCheckoutSite(organization: string, site: api.Site): SiteCheckoutState {
    const { screen: currentScreen } = useNavigationState();
    const { data: checkout } = useSuspenseQuery(
        apiQuery('previewSitePublishCheckout', { organizationId: organization, plan: site.type })
    );

    return React.useMemo(() => {
        // No checkout required, let the user publish immediately.
        if (checkout.type === 'go-ahead') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & pay',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            const { data: publishResult } = await apiClient.sites.publishSite(
                                organization,
                                site.id
                            );
                            // For go-ahead, the result should always be a Site
                            return publishResult as api.Site;
                        }, 'Error while publishing site')) ?? null
                    );
                },
            };
        }
        if (checkout.type === 'trial-available' || checkout.type === 'extended-trial-available') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & start trial',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            const { data: publishResult } = await apiClient.sites.publishSite(
                                organization,
                                site.id
                            );
                            // For trial-available, the result should always be a Site
                            return publishResult as api.Site;
                        }, 'Error while publishing site')) ?? null
                    );
                },
            };
        }

        // At this point, an upgrade is required.
        if (checkout.type === 'checkout') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Upgrade',
                    kind: 'upgrade',
                    leadingIcon: Icon.Upgrade,
                    href: getUpgradeScreenNavigationTarget({
                        organizationId: organization,
                        reason: 'checkout_site',
                        publishSiteId: site.id,
                        returnScreen: currentScreen,
                    }),
                },
                onPress: async () => {
                    analytics.logEvent(
                        analytics.createOrgEvent(organization, {
                            type: 'organization_open_upgrade',
                            currentFeaturesSet: null,
                            reason: 'checkout_site',
                        })
                    );
                    return null;
                },
            };
        }

        throw new Error('unknown checkout type');
    }, [checkout, organization, site.id, currentScreen]);
}
